'use client';

import React, { useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { TextInput, Button, Box, CheckmarkButton } from '@instamenu/ui-system-next';
import { captureClientException } from '@instamenu/posthog/client';
import { buttonPosition, discountCouponWrapper, inputWrapper } from './discount-coupon.css';
import { palette } from '@instamenu/ui-vanilla-extract';
import { useCheckoutContext } from '@instamenu/context-checkout';
import { Confetti, ConfettiRefProps } from '../confetti/confetti';

export const DiscountCoupon: React.FC = observer(() => {
  const [isLoading, setIsLoading] = useState(false);
  const order = useCheckoutContext()?.order;
  const [code, setCode] = useState(order?.discount?.code ?? '');
  const inputRef = useRef<HTMLDivElement>(null);
  const confettiRef = useRef<ConfettiRefProps>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || isLoading) return;
    setIsLoading(true);

    try {
      await order?.addDiscount(code);
      if (order?.error) {
        setCode('');
      } else {
        fireConfetti();
      }
    } catch (err) {
      captureClientException(err, {
        source: 'ui-storefront.discount-coupon.apply',
        properties: {
          code,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearDiscount = () => {
    try {
      order?.addDiscount(null);
      setCode('');
    } catch (err) {
      captureClientException(err, {
        source: 'ui-storefront.discount-coupon.clear',
      });
    }
  };

  const fireConfetti = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const originX = (rect.left + rect.width / 2) / window.innerWidth;
      const originY = rect.bottom / window.innerHeight;
      confettiRef.current?.fire(originX, originY);
    }
  }, []);

  return (
    <>
      <div className={discountCouponWrapper} ref={inputRef}>
        <div className={inputWrapper}>
          <TextInput
            placeholder="Enter discount code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            css={{
              borderColor: order?.discount?.id ? palette.success : undefined,
            }}
          />
        </div>
        <Box className={buttonPosition}>
          {order?.discount?.code ? (
            <CheckmarkButton css={{ width: 48 }} onClick={handleClearDiscount} />
          ) : (
            <Button onClick={handleSubmit} disabled={!code || isLoading} size="xs">
              Apply
            </Button>
          )}
        </Box>
      </div>
      <Confetti ref={confettiRef} />
    </>
  );
});
